import React, { useMemo } from "react"
import { Table, Tag, Card, Empty, Tabs, Tooltip } from "antd"
import { GridLoadSequenceItem, OutOfFormatBox } from "../types"
import Strings from "utils/strings"

interface GridLoadSequenceProps {
	gridLoadSequence?: GridLoadSequenceItem[]
	outOfFormatBoxes?: OutOfFormatBox[]
	loadSequenceType?: "grid" | "pile" | "line"
}

const { TabPane } = Tabs

const GridLoadSequence: React.FC<GridLoadSequenceProps> = ({
	gridLoadSequence,
	outOfFormatBoxes = [],
	loadSequenceType = "grid",
}) => {
	// Transform data for table
	const tableData = useMemo(() => {
		if (!gridLoadSequence || gridLoadSequence.length === 0) return []

		return gridLoadSequence.map((item, index) => ({
			key: index,
			...item,
			orderId: item.orderId?.$oid,
			// Convert string positions/piles to numbers for sorting if they're numeric strings
			position:
				typeof item.position === "string"
					? parseInt(item.position, 10)
					: item.position,
			pile:
				typeof item.pile === "string" && /^\d+$/.test(item.pile)
					? parseInt(item.pile, 10)
					: item.pile,
		}))
	}, [gridLoadSequence])

	// Define columns based on load sequence type
	const columns = useMemo(() => {
		const baseColumns = [
			{
				title: Strings.orders.boxId,
				dataIndex: "boxId",
				key: "boxId",
				width: 120,
				render: (text: string) => (
					<Tag color="blue" style={{ fontWeight: 500 }}>
						{text}
					</Tag>
				),
			},
		]

		// Add type-specific columns
		if (loadSequenceType === "grid") {
			return [
				...baseColumns,
				{
					title: Strings.orders.line,
					dataIndex: "line",
					key: "line",
					width: 80,
					render: (text: number) => (
						<span style={{ fontWeight: 500 }}>{text}</span>
					),
				},
				{
					title: Strings.orders.column,
					dataIndex: "column",
					key: "column",
					width: 80,
					render: (text: number) => (
						<span style={{ fontWeight: 500 }}>{text}</span>
					),
				},
				{
					title: Strings.orders.depth,
					dataIndex: "depth",
					key: "depth",
					width: 110,
					render: (text: number) => (
						<span style={{ fontWeight: 500 }}>{text}</span>
					),
				},
				{
					title: Strings.fields.weight,
					dataIndex: "boxWeight",
					key: "boxWeight",
					width: 90,
					render: (text: number) => <span>{text} kg</span>,
				},
			]
		} else if (loadSequenceType === "pile") {
			return [
				{
					title: Strings.orders.position,
					dataIndex: "position",
					key: "position",
					width: 100,
					sorter: (a: any, b: any) => {
						const aPos =
							typeof a.position === "number"
								? a.position
								: parseInt(a.position) || 0
						const bPos =
							typeof b.position === "number"
								? b.position
								: parseInt(b.position) || 0
						return aPos - bPos
					},
					defaultSortOrder: "descend" as const,
					render: (text: number | string) => (
						<Tag color="blue" style={{ fontWeight: 500 }}>
							{text || "-"}
						</Tag>
					),
				},
				{
					title: Strings.orders.pile,
					dataIndex: "pile",
					key: "pile",
					width: 200,
					render: (text: number | string) => {
						if (!text) return <span style={{ color: "#bfbfbf" }}>-</span>
						return <span style={{ fontWeight: 500 }}>{text}</span>
					},
				},
			]
		} else {
			// line type
			return [
				...baseColumns,
				{
					title: "Position",
					dataIndex: "line",
					key: "line",
					width: 100,
					sorter: (a: any, b: any) => a.line - b.line,
					render: (text: number) => (
						<span style={{ fontWeight: 500 }}>{text}</span>
					),
				},
				{
					title: Strings.fields.weight,
					dataIndex: "boxWeight",
					key: "boxWeight",
					width: 90,
					render: (text: number) => <span>{text} kg</span>,
				},
			]
		}
	}, [loadSequenceType])

	// Calculate summary statistics
	const summary = useMemo(() => {
		if (!gridLoadSequence || gridLoadSequence.length === 0) return null

		const totalBoxes = gridLoadSequence.length
		const totalWeight = gridLoadSequence.reduce(
			(sum, item) => sum + (item.boxWeight || 0),
			0
		)

		return {
			totalBoxes,
			totalWeight,
		}
	}, [gridLoadSequence])

	// Create 3D grid visualization for grid type
	const gridVisualization = useMemo(() => {
		if (
			loadSequenceType !== "grid" ||
			!gridLoadSequence ||
			gridLoadSequence.length === 0
		)
			return null

		// Find grid dimensions
		const maxLine = Math.max(
			...gridLoadSequence
				.map((item) => item.line || 0)
				.filter((line) => line > 0)
		)
		const maxColumn = Math.max(
			...gridLoadSequence
				.map((item) => item.column || 0)
				.filter((col) => col > 0)
		)
		const maxDepth = Math.max(
			...gridLoadSequence
				.map((item) => item.depth || 0)
				.filter((depth) => depth > 0)
		)

		// Create a map of positions to items
		const positionMap = new Map<string, GridLoadSequenceItem>()
		gridLoadSequence.forEach((item) => {
			if (item.line && item.column && item.depth) {
				const key = `${item.line}-${item.column}-${item.depth}`
				positionMap.set(key, item)
			}
		})

		// Render grid layers by depth
		return (
			<div style={{ marginBottom: 16 }}>
				<div
					style={{
						fontSize: 12,
						color: "#8c8c8c",
						marginBottom: 8,
						fontWeight: 500,
					}}
				>
					{Strings.orders.gridVisualization}
				</div>
				<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
					{Array.from({ length: maxDepth }, (_, depthIndex) => {
						const depth = depthIndex + 1
						return (
							<div key={depth}>
								<div
									style={{
										fontSize: 11,
										color: "#8c8c8c",
										marginBottom: 6,
										fontWeight: 500,
									}}
								>
									{Strings.orders.depth} {depth}
								</div>
								<div
									style={{
										display: "inline-grid",
										gridTemplateColumns: `repeat(${maxColumn}, 60px)`,
										gap: 4,
										padding: 8,
										background: "#fafafa",
										borderRadius: 4,
										border: "1px solid #e8e8e8",
									}}
								>
									{Array.from({ length: maxLine }, (_, lineIndex) => {
										const line = lineIndex + 1
										return Array.from({ length: maxColumn }, (_, colIndex) => {
											const column = colIndex + 1
											const key = `${line}-${column}-${depth}`
											const item = positionMap.get(key)

											const tooltipContent = item ? (
												<div>
													<div>
														<strong>{Strings.orders.boxId}:</strong>{" "}
														{item.boxId}
													</div>
													<div>
														<strong>{Strings.fields.weight}:</strong>{" "}
														{item.boxWeight} kg
													</div>
													<div>
														<strong>{Strings.orders.position}:</strong> L
														{item.line} C{item.column} D{item.depth}
													</div>
												</div>
											) : (
												<div>
													{Strings.orders.emptyPosition}
													<br />
													{Strings.orders.line} {line}, {Strings.orders.column}{" "}
													{column}
												</div>
											)

											return (
												<Tooltip
													key={key}
													title={tooltipContent}
													placement="top"
													mouseEnterDelay={0.3}
												>
													<div
														style={{
															width: 60,
															height: 60,
															border: "1px solid #d9d9d9",
															borderRadius: 4,
															display: "flex",
															flexDirection: "column",
															alignItems: "center",
															justifyContent: "center",
															background: item ? "#1890ff" : "white",
															color: item ? "white" : "#bfbfbf",
															fontSize: 10,
															fontWeight: 500,
															cursor: item ? "pointer" : "default",
															transition: "all 0.2s",
															position: "relative",
														}}
														onMouseEnter={(e) => {
															if (item) {
																e.currentTarget.style.transform = "scale(1.1)"
																e.currentTarget.style.zIndex = "10"
																e.currentTarget.style.boxShadow =
																	"0 4px 12px rgba(0,0,0,0.15)"
															}
														}}
														onMouseLeave={(e) => {
															if (item) {
																e.currentTarget.style.transform = "scale(1)"
																e.currentTarget.style.zIndex = "1"
																e.currentTarget.style.boxShadow = "none"
															}
														}}
													>
														{item ? (
															<>
																<div style={{ fontSize: 9, opacity: 0.9 }}>
																	{item.boxId}
																</div>
																<div style={{ fontSize: 8, opacity: 0.8 }}>
																	{item.boxWeight}kg
																</div>
															</>
														) : (
															<div style={{ fontSize: 9 }}>-</div>
														)}
													</div>
												</Tooltip>
											)
										})
									})}
								</div>
							</div>
						)
					})}
				</div>
				<div
					style={{
						marginTop: 12,
						padding: "8px 12px",
						background: "#f0f7ff",
						borderRadius: 4,
						fontSize: 11,
						color: "#666",
					}}
				>
					<strong>{Strings.orders.legend}:</strong>{" "}
					{Strings.orders.gridLegendText}
				</div>
			</div>
		)
	}, [gridLoadSequence, loadSequenceType])

	if (!gridLoadSequence || gridLoadSequence.length === 0) {
		return (
			<Card size="small">
				<Empty
					description={Strings.orders.noLoadSequenceData}
					image={Empty.PRESENTED_IMAGE_SIMPLE}
				/>
			</Card>
		)
	}

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			{summary && (
				<div
					style={{
						display: "flex",
						gap: 16,
						padding: "12px 16px",
						background: "#f5f5f5",
						borderRadius: 4,
						fontSize: 13,
					}}
				>
					<div>
						<span style={{ color: "#8c8c8c" }}>
							{Strings.orders.totalBoxes}:{" "}
						</span>
						<strong>{summary.totalBoxes}</strong>
					</div>
					<div>
						<span style={{ color: "#8c8c8c" }}>
							{Strings.orders.totalWeight}:{" "}
						</span>
						<strong>{summary.totalWeight} kg</strong>
					</div>
					<div>
						<span style={{ color: "#8c8c8c" }}>
							{Strings.orders.loadType}:{" "}
						</span>
						<Tag color="default">{loadSequenceType.toUpperCase()}</Tag>
					</div>
					{outOfFormatBoxes.length > 0 && (
						<div>
							<span style={{ color: "#8c8c8c" }}>
								{Strings.routes.outOfFormat}:{" "}
							</span>
							<Tag color="default">{outOfFormatBoxes.length}</Tag>
						</div>
					)}
				</div>
			)}

			{loadSequenceType === "grid" ? (
				<Tabs defaultActiveKey="visual" type="card" size="small">
					<TabPane tab={Strings.orders.visualGrid} key="visual">
						{gridVisualization}
					</TabPane>
					<TabPane tab={Strings.orders.tableView} key="table">
						<Table
							dataSource={tableData}
							columns={columns}
							size="small"
							pagination={false}
							scroll={{ x: "max-content", y: 400 }}
							bordered
						/>
					</TabPane>
				</Tabs>
			) : (
				<Table
					dataSource={tableData}
					columns={columns}
					size="small"
					pagination={false}
					scroll={{ x: "max-content", y: 400 }}
					bordered
				/>
			)}

			{outOfFormatBoxes.length > 0 && (
				<Card
					size="small"
					title={
						<span style={{ fontWeight: 600 }}>
							{Strings.routes.outOfFormatBoxes}
						</span>
					}
				>
					<div
						style={{
							display: "flex",
							flexWrap: "wrap",
							gap: 8,
						}}
					>
						{outOfFormatBoxes.map((box, index) => (
							<Tag key={index} color="default" style={{ margin: 0 }}>
								{box.boxId}
							</Tag>
						))}
					</div>
				</Card>
			)}
		</div>
	)
}

export default GridLoadSequence
